import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// --- Revenue Calculation Utils ---
const calcEstRevenue = (bookings, avgTicket, showRate = 0.8) => {
  return Math.round(bookings * avgTicket * showRate);
};

const formatRevenue = (amount) => {
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${amount}`;
};

const SCENARIOS = [
  {
    id: "dental",
    label: "BrightSmile Dental",
    industry: "Dental Clinic",
    location: "Austin, TX",
    callsAnswered: 132,
    bookings: 48,
    missed: 3,
    avgTicket: 150,
    convo: {
      caller1: "Hi, I'd like to book a cleaning appointment next week.",
      ai: "I can help with that. Do you prefer mornings or afternoons?",
      caller2: "Afternoons, any day after 3 PM works.",
      slot: "Thursday • 3:30 PM • Dr. Chen",
      slotMeta: "Cleaning • New patient"
    }
  },
  {
    id: "medspa",
    label: "Serenity Med Spa",
    industry: "Med Spa",
    location: "Los Angeles, CA",
    callsAnswered: 96,
    bookings: 34,
    missed: 2,
    avgTicket: 300,
    convo: {
      caller1: "Do you have any Botox appointments this Friday?",
      ai: "Yes, I can check that. Are you looking for morning or evening?",
      caller2: "Evening, after 5 PM.",
      slot: "Friday • 6:10 PM • Nurse Taylor",
      slotMeta: "Botox consultation"
    }
  },
  {
    id: "auto",
    label: "Prime Auto Repair",
    industry: "Auto Repair",
    location: "Chicago, IL",
    callsAnswered: 78,
    bookings: 29,
    missed: 5,
    avgTicket: 220,
    convo: {
      caller1: "My check engine light is on, can I bring the car tomorrow?",
      ai: "Sure. Is morning drop-off okay, or do you prefer afternoon?",
      caller2: "Morning is fine, before 10 AM.",
      slot: "Tomorrow • 9:15 AM • Full diagnostic",
      slotMeta: "Drop-off appointment"
    }
  },
  {
    id: "veterinary",
    label: "Happy Paws Veterinary",
    industry: "Veterinary Clinic",
    location: "Seattle, WA",
    callsAnswered: 95,
    bookings: 29,
    missed: 2,
    avgTicket: 180,
    convo: {
      caller1: "My dog needs a checkup, do you have availability this week?",
      ai: "Of course! What day works best for you?",
      caller2: "Wednesday afternoon would be perfect.",
      slot: "Wednesday • 2:00 PM • Dr. Martinez",
      slotMeta: "Annual checkup • Vaccination"
    }
  }
];

// simple animated number hook
const useAnimatedNumber = (target, duration = 600) => {
  const [value, setValue] = useState(target);

  useEffect(() => {
    let frameId;
    let startTime = null;
    const initial = value;
    const diff = target - initial;

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const next = initial + diff * progress;
      setValue(Math.round(next));
      if (progress < 1) {
        frameId = requestAnimationFrame(step);
      }
    };

    frameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameId);
  }, [target, duration, value]);

  return value;
};

export const HeroDashboardAnimated = () => {
  const [index, setIndex] = useState(0);
  const scenario = SCENARIOS[index];

  // auto-rotate scenarios every 7 seconds
  useEffect(() => {
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % SCENARIOS.length);
    }, 7000);
    return () => clearInterval(id);
  }, []);

  // Calculate revenue dynamically based on bookings and avg ticket
  const revenueRaw = calcEstRevenue(scenario.bookings, scenario.avgTicket);
  
  const calls = useAnimatedNumber(scenario.callsAnswered);
  const bookings = useAnimatedNumber(scenario.bookings);
  const missed = useAnimatedNumber(scenario.missed);
  const revenue = useAnimatedNumber(revenueRaw);

  return (
    <motion.div
      className="w-full max-w-xl rounded-3xl bg-white/95 border-2 border-blue-200 shadow-[0_24px_80px_rgba(99,102,241,0.25)] backdrop-blur-xl p-5 md:p-6 flex flex-col gap-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-slate-700 font-bold flex items-center gap-2">
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </span>
            Live Dashboard
          </p>
          <p className="text-[11px] text-slate-600 mt-0.5 font-medium">Real-time activity</p>
        </div>
        <div className="flex flex-col items-end text-right">
          <AnimatePresence mode="wait">
            <motion.div
              key={scenario.id}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.25 }}
              className="text-xs rounded-full bg-gradient-to-r from-blue-500 to-purple-500 px-3 py-1 flex items-center gap-2 shadow-md"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse shadow-sm" />
              <span className="font-bold text-white">{scenario.industry}</span>
              <span className="text-blue-100">• {scenario.location}</span>
            </motion.div>
          </AnimatePresence>
          <p className="mt-1 text-[11px] text-slate-600 font-medium">{scenario.label}</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Calls answered */}
        <StatCard
          label="Calls Answered"
          value={calls}
          badge="+12%"
          gradient="from-sky-500/30 via-sky-400/10 to-slate-900"
          barClass="bg-sky-400"
        />
        {/* Bookings */}
        <StatCard
          label="Bookings Made"
          value={bookings}
          badge="+8%"
          gradient="from-emerald-500/30 via-emerald-400/10 to-slate-900"
          barClass="bg-emerald-400"
        />
        {/* Missed */}
        <StatCard
          label="Missed Calls"
          value={missed}
          badge="-75%"
          gradient="from-amber-500/30 via-amber-400/10 to-slate-900"
          barClass="bg-amber-400"
        />
        {/* Revenue */}
        <StatCard
          label="Est. Revenue"
          value={formatRevenue(revenue)}
          badge="+32%"
          gradient="from-fuchsia-500/30 via-fuchsia-400/10 to-slate-900"
          barClass="bg-fuchsia-400"
        />
      </div>

      {/* Conversation */}
      <div className="mt-1 space-y-2">
        <p className="text-[11px] uppercase tracking-[0.16em] text-slate-700 font-bold">
          Active Conversation
        </p>
        <div className="space-y-1">
          <ChatBubble side="left" author="Caller" timestamp="Just now">
            {scenario.convo.caller1}
          </ChatBubble>
          <ChatBubble side="right" author="AI" timestamp="Just now" accent>
            {scenario.convo.ai}
          </ChatBubble>
          <ChatBubble side="left" author="Caller" timestamp="Just now">
            {scenario.convo.caller2}
          </ChatBubble>
        </div>
      </div>

      {/* Slot card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={scenario.id + "-slot"}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.3 }}
          className="mt-3 rounded-2xl bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 px-4 py-3 flex items-center justify-between gap-3 shadow-lg"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="text-sm">
              <p className="font-bold text-slate-900">Available Slot Found</p>
              <p className="text-[12px] text-emerald-700 font-semibold">{scenario.convo.slot}</p>
              <p className="text-[11px] text-slate-600">{scenario.convo.slotMeta}</p>
            </div>
          </div>
          <button className="shrink-0 rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-bold text-white shadow-md hover:bg-emerald-600 transition">
            Confirm
          </button>
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

const StatCard = ({ label, value, badge, gradient, barClass }) => {
  return (
    <motion.div
      layout
      className="rounded-2xl bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 px-4 py-3 flex flex-col gap-2 shadow-lg"
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-700 font-bold">{label}</p>
        <span className="text-[10px] rounded-full bg-emerald-500 px-2 py-0.5 text-white font-bold shadow-sm">
          {badge}
        </span>
      </div>
      <p className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{value}</p>
      <div className="mt-1 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${barClass}`}
          initial={{ width: "0%" }}
          animate={{ width: "78%" }}
          transition={{ duration: 0.8 }}
        />
      </div>
    </motion.div>
  );
};

const ChatBubble = ({ side, author, timestamp, children, accent }) => {
  const isRight = side === "right";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex items-end gap-2 ${isRight ? "justify-end" : ""}`}
    >
      {!isRight && (
        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-[11px] text-white font-bold shadow-md">
          C
        </div>
      )}
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 text-[13px] ${
          accent
            ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/40"
            : "bg-slate-100 text-slate-900 border border-slate-200"
        }`}
      >
        <p className={`text-[10px] uppercase tracking-[0.14em] mb-0.5 font-semibold ${
          accent ? "text-white/80" : "text-slate-500"
        }`}>
          {author} • {timestamp}
        </p>
        <p className="leading-snug font-medium">{children}</p>
      </div>
      {isRight && (
        <div className="h-7 w-7 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-[11px] font-bold text-white shadow-md">
          AI
        </div>
      )}
    </motion.div>
  );
};
